import { FC, ReactNode, useState, KeyboardEvent } from 'react';
import { Theme } from '@mui/material/styles';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import MuiDialogTitle from '@mui/material/DialogTitle';
import MuiDialogContent from '@mui/material/DialogContent';
import MuiDialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import { DeloitteButton } from './DeloitteButton';
import { CircularProgress } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';

const styles = (theme: Theme) =>
    createStyles({
        root: {
            margin: 0
        },
        closeButton: {
            position: 'absolute',
            right: theme.spacing(1),
            top: theme.spacing(1),
            color: theme.palette.grey[500],
        },
    });

export interface DialogTitleProps extends WithStyles<typeof styles> {
    id: string;
    children: ReactNode;
    onClose: () => void;
}

const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
    const { children, classes, onClose, ...other } = props;
    return (
        <MuiDialogTitle className={classes.root} {...other}>
            <Grid container spacing={6}>
                <Grid>
                    <Typography variant="h6">{children}</Typography>
                </Grid>

                {onClose ? (
                    <Grid>
                        <IconButton
                            aria-label="close"
                            className={classes.closeButton}
                            onClick={onClose}
                            size="large">
                            <CloseIcon />
                        </IconButton>
                    </Grid>
                ) : null}
            </Grid>
        </MuiDialogTitle>
    );
});

const DialogContent = withStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(2),
    },
}))(MuiDialogContent);

const DialogActions = withStyles((theme: Theme) => ({
    root: {
        margin: 0,
        padding: theme.spacing(1),
    },
}))(MuiDialogActions);


interface IActionItem {
    label: string;
    action: () => void;
}
export interface DeloitteDialogProps extends DialogProps {
    title: string;
    open: boolean;
    actionTitle?: string;
    closeCallback: (...params: any[]) => void;
    bottomAction?: () => Promise<void>;
    actions?: IActionItem[];
    skipCallback?: boolean;
    disabled?: boolean;
}

export const DeloitteDialog: FC<DeloitteDialogProps> = (props) => {
    const { title, children, open, closeCallback, actionTitle, bottomAction, actions, skipCallback, disabled, ...others } = props;
    const [loading, setLoading] = useState(false);

    const executeAsyncAction = async () => {
        setLoading(true);
        if (bottomAction)
            await bottomAction();
        setLoading(false);
        if (!skipCallback)
            closeCallback();
    }
    const keyPressed = (event: KeyboardEvent) => {
        //if (event.key === "Enter")
        //    executeAsyncAction();
        if (event.key === "Escape")
            closeCallback();
    }

    return (
        <Dialog onClose={closeCallback} aria-labelledby="customized-dialog-title" open={open} onKeyPress={keyPressed} {...others}>
            <DialogTitle id="customized-dialog-title" onClose={closeCallback}>
                {title}
            </DialogTitle>
            <DialogContent dividers={children ? true : false}>
                {children}
            </DialogContent>
            <DialogActions>
                {
                    actions && actions.map((item, index) => {
                        return (<DeloitteButton key={index} onClick={item.action} variant="contained" color="secondary" cta disabled={loading || disabled}>
                            {!loading &&
                                (item.label)
                            }
                            {loading &&
                                <CircularProgress />
                            }
                        </DeloitteButton>
                        )
                    })
                }
                <DeloitteButton onClick={executeAsyncAction} variant="contained" color="secondary" cta disabled={loading || disabled} fullWidth={!actions || actions.length === 0}>
                    {!loading &&
                        (actionTitle ?? "Fermer")
                    }
                    {loading &&
                        <CircularProgress />
                    }
                </DeloitteButton>
            </DialogActions>
        </Dialog>
    );
}